import React from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { PLAN_FEATURES } from "@/lib/planFeatures";

/**
 * One plan, drawn identically for all three (PLANES M4a).
 *
 * The plans differ only in quotes a month, so the card has exactly one row
 * that changes in kind — the quotes, as the hero — plus the name and the price.
 * No per-plan colour, ring, ribbon, tag or icon: those were how the old cards
 * said "this one is better", and after PLANES that is not a claim any card may
 * make. `/pricing` and `/provider/billing` both draw plans through this
 * component and nothing else; smoke_layout rule 8 holds them to it.
 *
 * Uniform size: the grid stretches every card to the row's height and the
 * card is a column (`h-full flex flex-col`); the footer is pushed to the
 * bottom (`mt-auto`) and reserves its own height, so a card whose footer says
 * less — the current plan, a courtesy — is not a shorter card. The badge slot
 * above the name is reserved whether or not a badge is drawn, for the same
 * reason. Stacked on a phone the same rules make three equal boxes.
 */
export default function PlanCard({ plan, badge = null, footer = null, testId }) {
  const { t } = useTranslation();
  return (
    <div
      className="card-base p-6 h-full flex flex-col"
      data-testid={testId || `plan-card-${plan.plan_id}`}
    >
      <div className="h-6 flex items-center justify-end">{badge}</div>
      <h3 className="font-display text-2xl font-bold text-worker">{plan.name}</h3>
      <p className="mt-2">
        <span className="font-mono text-3xl font-bold text-worker">
          ${Number(plan.price_monthly_usd || 0).toFixed(0)}
        </span>
        <span className="text-smoke text-sm">{t("common.perMonth")}</span>
      </p>

      <div className="mt-6 flex items-baseline gap-2" data-testid={`plan-quotes-${plan.plan_id}`}>
        <span className="font-mono text-6xl font-extrabold leading-none text-worker">
          {plan.included_quotes_per_month}
        </span>
        <span className="font-display text-base font-semibold text-worker">
          {t("planCards.quotesLabel", { count: Number(plan.included_quotes_per_month) || 0 })}
        </span>
      </div>

      <ul className="mt-6 space-y-2" data-testid={`plan-features-${plan.plan_id}`}>
        {PLAN_FEATURES.map((f) => (
          <li key={f.id} className="flex items-start gap-2 text-sm text-worker">
            <Check size={16} className="text-hive-ink shrink-0 mt-0.5" />
            <span>{t(`planFeatures.${f.labelKey}`)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-6 min-h-[7rem] flex flex-col justify-end">
        {footer}
      </div>
    </div>
  );
}
